import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ShieldCheck, Truck, Sparkles, DollarSign, Users, Handshake,
  ArrowRight, LogIn, UserPlus, CheckCircle2, Award, Briefcase
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const AffiliateHub = () => {
  const expectItems = [
    { icon: ShieldCheck, title: 'Verified Industry Partners', body: 'Access trusted companies and service providers selected to bring real value to the box truck industry.' },
    { icon: Briefcase, title: 'Business Resources That Matter', body: 'Find tools and services that help streamline operations, improve efficiency, and support day-to-day business growth.' },
    { icon: Sparkles, title: 'Exclusive Member Opportunities', body: 'Gain access to partner offers, promotions, and resources tailored to the Straight Connect community.' },
    { icon: DollarSign, title: 'Earn With Straight Connect', body: 'Members and approved partners can participate in affiliate opportunities that reward valuable referrals while helping strengthen the trucking network.' },
  ];

  const whyItems = [
    'Save time finding trusted vendors',
    'Access discounts & business tools',
    'Earn referral income from services you already recommend',
    'Support other verified businesses in the community',
  ];

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 text-white">
        <div className="absolute inset-0 opacity-25">
          <img
            src="https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&w=2000&q=70"
            alt="Diverse trucking professionals"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/70 to-transparent" />
        </div>
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-orange-500/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-20 w-[28rem] h-[28rem] bg-blue-500/20 rounded-full blur-3xl" />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-500/15 border border-orange-500/30 backdrop-blur rounded-full text-xs font-semibold text-orange-300 mb-6 tracking-wider uppercase">
              <Award className="h-3.5 w-3.5" /> Straight Connect Affiliate Marketplace
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4 leading-[1.05]">
              Straight Connect <span className="text-orange-400">Affiliate Hub</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 mb-2 font-medium">
              Verified Partners. Real Resources. Real Earnings.
            </p>
            <p className="text-sm sm:text-base text-slate-400 max-w-2xl mb-8 leading-relaxed">
              The Box Truck Industry&apos;s Verified Affiliate Marketplace — find trusted tools, services, and resources built for the box truck community, and earn by referring businesses you trust.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/affiliate/join" data-testid="affiliate-hero-signup-btn"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-md transition-all shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 hover:-translate-y-0.5">
                <UserPlus className="h-5 w-5" /> Sign Up Affiliate
              </Link>
              <Link to="/affiliate/login" data-testid="affiliate-hero-signin-btn"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white/10 hover:bg-white/15 border border-white/20 text-white font-semibold rounded-md backdrop-blur transition-all">
                <LogIn className="h-5 w-5" /> Sign In Affiliate
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <main className="flex-1">
        {/* IMAGE STRIP */}
        <section className="bg-white border-b border-slate-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&w=600&q=70',
                'https://images.unsplash.com/photo-1591768793355-74d04bb6608f?auto=format&fit=crop&w=600&q=70',
                'https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&w=600&q=70',
                'https://images.unsplash.com/photo-1736019510155-d2c9f60bb0d7?auto=format&fit=crop&w=600&q=70',
              ].map((src, i) => (
                <div key={i} className="aspect-[4/3] rounded-xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all">
                  <img src={src} alt="Box truck community" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* BODY DESCRIPTION */}
        <section className="bg-white py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 prose prose-slate">
            <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
              The Straight Connect Affiliate Marketplace is a dedicated space designed to connect box truck professionals with trusted industry partners, essential tools, and business resources that support long-term success on and off the road.
            </p>
            <p className="text-base sm:text-lg text-slate-700 leading-relaxed mt-5">
              Built specifically for the box truck community, this marketplace highlights verified companies, services, and products that drivers, owner-operators, and fleet owners can rely on — from dispatch and factoring to insurance, equipment, fuel savings, compliance tools, and business growth solutions.
            </p>
            <p className="text-base sm:text-lg text-slate-700 leading-relaxed mt-5">
              Our goal is simple: create one trusted place where members can discover reliable industry resources while also creating opportunities to grow with Straight Connect.
            </p>
          </div>
        </section>

        {/* WHAT MEMBERS CAN EXPECT */}
        <section className="bg-slate-50 py-16 border-y border-slate-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-2 text-center">What Members Can Expect</h2>
            <p className="text-slate-500 text-center mb-12 text-sm sm:text-base">Four pillars that make the Affiliate Hub uniquely built for the box truck community.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {expectItems.map((item, i) => (
                <motion.div key={item.title}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.05 }} viewport={{ once: true }}
                  className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all">
                  <div className="w-11 h-11 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                    <item.icon className="h-5 w-5 text-orange-600" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{item.body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* WHY IT MATTERS */}
        <section className="bg-white py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-5">Why It Matters</h2>
              <p className="text-slate-700 leading-relaxed mb-5">
                Straight Connect was built to create meaningful connections within the transportation industry. The Affiliate Marketplace extends that mission by giving members access to reliable partners and resources in one centralized platform — making it easier to connect, grow, and move forward with confidence.
              </p>
              <p className="text-slate-700 leading-relaxed">
                Whether you&apos;re starting your first box truck business, expanding your fleet, or looking for dependable industry solutions, the Straight Connect Affiliate Marketplace is built to help keep your business moving.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1577563908411-5077b6dc7624?auto=format&fit=crop&w=1200&q=70"
                alt="Driver inspecting box truck"
                className="rounded-2xl shadow-xl w-full"
              />
              <div className="absolute -bottom-6 -left-4 bg-white rounded-xl shadow-xl border border-slate-100 p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center"><Handshake className="h-5 w-5 text-white" /></div>
                <div>
                  <p className="text-xs font-bold text-slate-900">Verified Partner</p>
                  <p className="text-[11px] text-slate-500">Trusted by the network</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WHY THIS PAGE EXISTS */}
        <section className="bg-slate-900 text-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4 text-center">Why This Page Exists</h2>
            <p className="text-slate-300 text-center mb-10 leading-relaxed max-w-2xl mx-auto">
              Straight Connect was built to help drivers, carriers, dispatchers, and small fleets connect with real people and verified resources. The Affiliate Hub helps members:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
              {whyItems.map(item => (
                <div key={item} className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-lg p-4 backdrop-blur">
                  <CheckCircle2 className="h-5 w-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-200">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="bg-gradient-to-br from-orange-500 to-orange-600 text-white py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3 leading-tight">
              Keep your wheels moving — and your opportunities growing.
            </h2>
            <p className="text-orange-50 text-base sm:text-lg mb-8 leading-relaxed">
              The Straight Connect Affiliate Marketplace connects you with trusted industry partners, exclusive offers, and earning opportunities made for the box truck community.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/affiliate/login" data-testid="affiliate-cta-signin-btn"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white text-orange-700 font-bold rounded-md hover:bg-orange-50 transition-colors shadow-lg">
                <LogIn className="h-5 w-5" /> Sign In Affiliate
              </Link>
              <Link to="/affiliate/join" data-testid="affiliate-cta-signup-btn"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-slate-900 text-white font-bold rounded-md hover:bg-slate-800 transition-colors shadow-lg">
                <UserPlus className="h-5 w-5" /> Sign Up Affiliate
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AffiliateHub;
